import React, { forwardRef } from 'react';
import styled from 'styled-components';
import { tokens } from '../../public/tokens';

const sizes = {
    xs: {
        trackWidth: '24px',
        trackHeight: '14px',
        thumbSize: '10px',
        gap: '2px',
        fontSize: tokens.typography.fontSize['md-12px'],
        lineHeight: '16px',
    },
    sm: {
        trackWidth: '28px',
        trackHeight: '16px',
        thumbSize: '12px',
        gap: '2px',
        fontSize: tokens.typography.fontSize['md-12px'],
        lineHeight: '16px',
    },
    md: {
        trackWidth: '34px',
        trackHeight: '20px',
        thumbSize: '16px',
        gap: '2px',
        fontSize: tokens.typography.fontSize['lg-14px'],
        lineHeight: '20px',
    },
    lg: {
        trackWidth: '40px',
        trackHeight: '24px',
        thumbSize: '20px',
        gap: '4px',
        fontSize: tokens.typography.fontSize['lg-14px'],
        lineHeight: '20px',
    },
};

const variantConfig = {
    primary: {
        background: tokens.colors.ui.primary,
        color: tokens.colors.ui['primary-foreground'],
        hover: { background: tokens.colors.ui.primary },
        active: { background: tokens.colors.ui.primary },
    },
    secondary: {
        background: tokens.colors.brand.blue['100'],
        color: tokens.colors.brand['aie-blue']['500'],
        hover: { opacity: 0.8 },
        active: { opacity: 0.8 },
    },
    destructive: {
        background: tokens.colors.states.alert['50'],
        color: tokens.colors.ui.destructive,
        hover: { background: tokens.colors.states.alert['100'] },
        active: { background: tokens.colors.states.alert['50'] },
    },
};

const getVariantStyles = (variant) => {
    const config = variantConfig[variant] || variantConfig.primary;
    const { background, color, hover, active } = config;
    return {
        background,
        color,
        hover,
        active,
    };
};

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
`;

const Track = styled.span(({ $size = 'lg', $variant = 'primary', $checked = false }) => {
    const { trackWidth, trackHeight, gap, thumbSize } = sizes[$size];
    const variantStyles = getVariantStyles($variant);

    return {
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        flexShrink: 0,
        width: trackWidth,
        height: trackHeight,
        borderRadius: '9999px',
        transition: 'all 0.2s ease',
        border: `1px solid ${tokens.colors.ui.border}`,
        background: tokens.colors.ui.background,
        cursor: 'pointer',
        // No boxShadow here - it should only appear on focus
        ...($checked && {
            background: variantStyles.background,
            borderColor: variantStyles.background,
        }),
        ...(!$checked && {
            background: tokens.colors.ui.background,
            borderColor: tokens.colors.ui.border,
        }),
        '&:hover': variantStyles.hover,
        '&:active': variantStyles.active,
        '& .thumb': {
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            left: $checked ? `calc(100% - ${thumbSize} - ${gap})` : gap,
            width: thumbSize,
            height: thumbSize,
            borderRadius: '50%',
            background: $checked ? variantStyles.color : tokens.colors.neutral.white,
            transition: 'all 0.2s ease',
            boxShadow: '0px 0px 0px 1px rgba(163, 163, 163, 0.50)',
        },
    };
});

const Wrapper = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: ${tokens.typography.fontFamilies['albert-sans']};
    font-size: ${({ $size = 'lg' }) => sizes[$size].fontSize};
    font-weight: ${tokens.typography.fontWeights.medium};
    line-height: ${({ $size = 'lg' }) => sizes[$size].lineHeight};
    color: ${tokens.colors.ui.foreground};
    cursor: pointer;

    &:has(input:disabled) {
        opacity: 0.5;
        cursor: not-allowed;
    }

    input:focus-visible + span {
        outline: 1px solid ${tokens.colors.brand.blue['500']};
        box-shadow: 0px 0px 0px 3px rgba(163, 163, 163, 0.5);
    }

    input[data-error='true'] + span {
        outline: 1px solid ${tokens.colors.ui.destructive};
        box-shadow: 0px 0px 1px 1px rgba(196, 61, 81, 0.2);
    }
`;

const LabelContainer = styled.span`
    display: flex;
    flex-direction: column;
`;

const LabelText = styled.span`
    color: ${({ $error }) => ($error ? tokens.colors.ui.destructive : tokens.colors.ui.foreground)};
`;

const Subtext = styled.span`
    font-size: ${tokens.typography.fontSize['md-12px']};
    font-weight: ${tokens.typography.fontWeights.regular};
    line-height: 16px;
    color: ${tokens.colors.ui['muted-foreground']};
`;

export const FieldWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
`;

export const Label = styled.label`
    font-size: ${tokens.typography.fontSize['lg-14px']};
    font-family: ${tokens.typography.fontFamilies['albert-sans']};
    font-weight: ${tokens.typography.fontWeights.medium};
    line-height: 20px;
    color: ${tokens.colors.ui.foreground};
    display: flex;
    align-items: center;
    gap: 4px;
    &[data-required='true']::after {
        content: '*';
        color: ${tokens.colors.ui.destructive};
        margin-left: 2px;
    }
`;

export const ErrorText = styled.span`
    font-size: ${tokens.typography.fontSize['md-12px']};
    font-family: ${tokens.typography.fontFamilies['albert-sans']};
    font-weight: ${tokens.typography.fontWeights.medium};
    line-height: 16px;
    color: ${tokens.colors.ui.destructive};
    min-height: 1.4em;
`;

export const HelperText = styled.span`
    font-size: ${tokens.typography.fontSize['md-12px']};
    font-family: ${tokens.typography.fontFamilies['albert-sans']};
    font-weight: ${tokens.typography.fontWeights.regular};
    line-height: 16px;
    color: ${tokens.colors.ui['muted-foreground']};
`;

/**
 * Toggle switch with an optional label and subtext.
 * @param {'xs'|'sm'|'md'|'lg'} [size] Defaults to lg
 * @param {'primary'|'secondary'|'destructive'} [variant] Defaults to primary
 * @param {boolean} [error] Applies the error outline
 */
const ToggleButton = forwardRef(
    (
        {
            checked = false,
            onChange,
            size = 'lg',
            variant = 'primary',
            disabled = false,
            error = false,
            label,
            subtext,
            style,
            ...props
        },
        ref,
    ) => {
        return (
            <Wrapper $size={size} style={style}>
                <HiddenCheckbox
                    ref={ref}
                    checked={checked}
                    onChange={onChange}
                    disabled={disabled}
                    data-error={error ? 'true' : undefined}
                    {...props}
                />
                <Track $size={size} $variant={variant} $checked={checked}>
                    <span className="thumb" />
                </Track>
                {(label || subtext) && (
                    <LabelContainer>
                        {label && <LabelText $error={error}>{label}</LabelText>}
                        {subtext && <Subtext>{subtext}</Subtext>}
                    </LabelContainer>
                )}
            </Wrapper>
        );
    },
);

ToggleButton.displayName = 'ToggleButton';

export default ToggleButton;
